import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Input, Field, Loader } from '@single-platform/components';
import {
  FarmersQuestionnaireHeader,
  PageSubtitle
} from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import countries from '@auth/components/countriesDropdown/farmersCountryList.json';
import { ButtonSectionWrapperStyle } from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { DropdownInput } from '@applicationKYC/FarmersQuestionnaire/components/dropdownInput/dropdownInput';
import { selectKycAppState } from '@redux';

const defaultValues = {
  country: null,
  city: null,
  address: null,
  postalCode: null
};

export const FarmerAddressPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        country: Yup.string().required(t('validation_required_field')).nullable(),
        city: Yup.string().required(t('validation_required_field')).nullable(),
        address: Yup.string().required(t('validation_required_field')).nullable(),
        postalCode: Yup.string().required(t('validation_required_field')).nullable()
      }),
    [t]
  );

  const existingValues = farmerInformation?.farmerSection?.farmersAddressPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        await onSubmit?.(data, 'farmersAddressPage');
      },
      () => setTouchedOnAll()
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader>
        <PageHeader>{t('farmers_farmer_address_header')}</PageHeader>
        <PageSubtitle>{t('farmers_farmer_address_subheader')}</PageSubtitle>
      </FarmersQuestionnaireHeader>
      <DropdownInput
        validationHelpers={validationHelpers}
        name="country"
        value={values.country}
        filterCountries={false}
        placeholder={t('farmers_farmer_country')}
        options={countries}
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={Input}
        name="city"
        value={values.city}
        placeholder={t('farmers_farmer_city')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={Input}
        name="address"
        value={values.address}
        placeholder={t('farmers_farmer_address')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={Input}
        name="postalCode"
        value={values.postalCode}
        placeholder={t('farmers_farmer_postalCode')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />

      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary={true}
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
